/** Localized AutoMapa UI copy — pass `t` from useLocale(). */

import {
  INVENTORY_FORMATS,
  LISTING_CHANNELS,
} from "../constants/automapDealerOnboardingConstants.js";
import { VEHICLE_SEARCH_SORT } from "../constants/vehicleSearchParamsConstants.js";
import { LISTING_FUEL_TYPE } from "../constants/automapListingConstants.js";
import {
  DEALER_PROFILE_FALLBACK_COPY,
  DEALER_PROFILE_PAGE_STATE,
  isDealerProfileLocationFallbackLabel,
} from "../constants/dealerProfileConstants.js";
import { formatTemplatePrice } from "../clientViewRuntime/templates/autoclaroHomeUtils.js";
import {
  BUDGET_OPTION_DEFS,
  CITY_OPTION_VALUES,
  FINANCING_OPTION_VALUES,
  FUEL_OPTION_VALUES,
  LOAN_TERM_OPTION_VALUES,
  TIMELINE_OPTION_VALUES,
  TRANSMISSION_OPTION_VALUES,
  USE_CASE_OPTION_VALUES,
  VEHICLE_TYPE_OPTION_VALUES,
} from "../clientViewRuntime/templates/buyerConciergeQuizConstants.js";
import { getUnknownAttributeLabel } from "./automotiveCardCopy.js";
import {
  buildSearchBudgetOptions,
  buildSearchMaxPriceOptions,
  buildSearchMinPriceOptions,
  formatCompactBudgetAmount,
} from "../utils/vehicleSearch/vehicleSearchPriceOptions.js";
import { getDealerDirectoryPartnershipAdCopy } from "./dealerDirectoryCopy.js";

export {
  getDealerDirectoryLinks,
  getDealerDirectoryPageCopy,
  getDealerDirectoryPartnershipAdCopy,
  getDealerDirectoryFilterCopy,
  getDealerDirectoryFeaturedCopy,
  getDealerDirectoryCardCopy,
  getDealerDirectoryCardInventoryLabel,
  getDealerDirectoryCardContactLabel,
  getDealerDirectoryCardVerificationLabel,
  getDealerDirectoryTrustChecklistItems,
  getDealerDirectoryBreadcrumbs,
} from "./dealerDirectoryCopy.js";

export {
  formatCardAvailabilityStatus,
  formatCardFreshnessLabel,
  formatCardLastConfirmed,
  getUnknownAttributeLabel,
} from "./automotiveCardCopy.js";

function formatPriceChip(value) {
  if (value == null) return null;
  return formatTemplatePrice(value, "COP").replace(" COP", "");
}

export function getDealerLandingPageCopy(t) {
  const valueBulletKeys = ["budget", "whatsapp", "verified", "reports", "visibility", "founding"];
  return {
    eyebrow: t("automotive.dealerLanding.eyebrow"),
    title: t("automotive.dealerLanding.title"),
    intro: t("automotive.dealerLanding.intro"),
    requestAccess: t("automotive.dealerLanding.requestAccess"),
    learnFreeLeads: t("automotive.dealerLanding.learnFreeLeads"),
    foundingOfferTitle: t("automotive.dealerLanding.foundingOfferTitle"),
    foundingOfferBody: t("automotive.dealerLanding.foundingOfferBody"),
    valueBullets: valueBulletKeys.map((key) => t(`automotive.dealerLanding.valueBullets.${key}`)),
    howItWorks: {
      heading: t("automotive.dealerLanding.howItWorks.heading"),
      intro: t("automotive.dealerLanding.howItWorks.intro"),
      steps: [
        {
          title: t("automotive.dealerLanding.howItWorks.step1Title"),
          body: t("automotive.dealerLanding.howItWorks.step1Body"),
        },
        {
          title: t("automotive.dealerLanding.howItWorks.step2Title"),
          body: t("automotive.dealerLanding.howItWorks.step2Body"),
        },
        {
          title: t("automotive.dealerLanding.howItWorks.step3Title"),
          body: t("automotive.dealerLanding.howItWorks.step3Body"),
        },
      ],
    },
    offer: {
      heading: t("automotive.dealerLanding.offer.heading"),
      receiveTitle: t("automotive.dealerLanding.offer.receiveTitle"),
      receiveItems: [
        t("automotive.dealerLanding.offer.receiveBudget"),
        t("automotive.dealerLanding.offer.receiveProfile"),
        t("automotive.dealerLanding.offer.receiveVisibility"),
        t("automotive.dealerLanding.offer.receiveReports"),
      ],
      provideTitle: t("automotive.dealerLanding.offer.provideTitle"),
      provideItems: [
        t("automotive.dealerLanding.offer.provideInventory"),
        t("automotive.dealerLanding.offer.provideWhatsapp"),
        t("automotive.dealerLanding.offer.provideUpdates"),
        t("automotive.dealerLanding.offer.provideFeedback"),
      ],
      freshnessTitle: t("automotive.dealerLanding.offer.freshnessTitle"),
      freshnessBody: t("automotive.dealerLanding.offer.freshnessBody"),
    },
    preview: {
      heading: t("automotive.dealerLanding.preview.heading"),
      intro: t("automotive.dealerLanding.preview.intro"),
    },
    testimonials: {
      heading: t("automotive.dealerLanding.testimonials.heading"),
      intro: t("automotive.dealerLanding.testimonials.intro"),
      quote: t("automotive.dealerLanding.testimonials.quote"),
      attribution: t("automotive.dealerLanding.testimonials.attribution"),
    },
    cta: {
      heading: t("automotive.dealerLanding.cta.heading"),
      body: t("automotive.dealerLanding.cta.body"),
    },
    footerTagline: (siteName) =>
      t("automotive.dealerLanding.shell.footerTagline", { siteName }),
  };
}

export function getDealerDemoRequestCopy(t) {
  return {
    eyebrow: t("automotive.dealerDemoRequest.eyebrow"),
    title: t("automotive.dealerDemoRequest.title"),
    intro: t("automotive.dealerDemoRequest.intro"),
    timeEstimate: t("automotive.dealerDemoRequest.timeEstimate"),
    asideSubtitle: t("automotive.dealerDemoRequest.asideSubtitle"),
    messagePlaceholder: t("automotive.dealerDemoRequest.messagePlaceholder"),
    fields: {
      businessName: t("automotive.dealerDemoRequest.fields.businessName"),
      contactName: t("automotive.dealerDemoRequest.fields.contactName"),
      contactRole: t("automotive.dealerDemoRequest.fields.contactRole"),
      whatsapp: t("automotive.dealerDemoRequest.fields.whatsapp"),
      email: t("automotive.dealerDemoRequest.fields.email"),
      city: t("automotive.dealerDemoRequest.fields.city"),
      address: t("automotive.dealerDemoRequest.fields.address"),
      estimatedInventoryCount: t("automotive.dealerDemoRequest.fields.estimatedInventoryCount"),
      inventoryFormat: t("automotive.dealerDemoRequest.fields.inventoryFormat"),
      offersFinancing: t("automotive.dealerDemoRequest.fields.offersFinancing"),
      listingChannels: t("automotive.dealerDemoRequest.fields.listingChannels"),
      message: t("automotive.dealerDemoRequest.fields.message"),
    },
    whatsappPlaceholder: t("automotive.dealerDemoRequest.whatsappPlaceholder"),
    consent: t("automotive.dealerDemoRequest.consent"),
    submit: t("automotive.dealerDemoRequest.submit"),
    submitting: t("automotive.dealerDemoRequest.submitting"),
    submitError: t("automotive.dealerDemoRequest.submitError"),
    validation: {
      required: t("automotive.dealerDemoRequest.validation.required"),
      consentRequired: t("automotive.dealerDemoRequest.validation.consentRequired"),
    },
    channelLabel: (channel) => t(`automotive.dealerDemoRequest.channels.${channel}`),
    formatLabel: (format) => t(`automotive.dealerDemoRequest.formats.${format}`),
    inventoryFormats: INVENTORY_FORMATS,
    listingChannels: LISTING_CHANNELS,
    financingOptions: [
      { value: "yes", label: t("automotive.dealerDemoRequest.financing.yes") },
      { value: "no", label: t("automotive.dealerDemoRequest.financing.no") },
      { value: "sometimes", label: t("automotive.dealerDemoRequest.financing.sometimes") },
    ],
    sections: {
      dealership: t("automotive.dealerDemoRequest.sections.dealership"),
      contact: t("automotive.dealerDemoRequest.sections.contact"),
      inventory: t("automotive.dealerDemoRequest.sections.inventory"),
      channels: t("automotive.dealerDemoRequest.sections.channels"),
    },
    listingChannelsHint: t("automotive.dealerDemoRequest.listingChannelsHint"),
    listingChannelsSelected: (count) =>
      t("automotive.dealerDemoRequest.listingChannelsSelected", { count }),
    selectAllChannels: t("automotive.dealerDemoRequest.selectAllChannels"),
    clearChannels: t("automotive.dealerDemoRequest.clearChannels"),
    success: {
      title: t("automotive.dealerDemoRequest.success.title"),
      body: t("automotive.dealerDemoRequest.success.body"),
      stepsHeading: t("automotive.dealerDemoRequest.success.stepsHeading"),
      steps: [
        t("automotive.dealerDemoRequest.success.step1"),
        t("automotive.dealerDemoRequest.success.step2"),
        t("automotive.dealerDemoRequest.success.step3"),
        t("automotive.dealerDemoRequest.success.step4"),
      ],
      backHome: t("automotive.dealerDemoRequest.success.backHome"),
      learnLeads: t("automotive.dealerDemoRequest.success.learnLeads"),
    },
    footerTagline: (siteName) =>
      t("automotive.dealerLanding.shell.footerTagline", { siteName }),
  };
}

const DEALER_FREE_LEADS_ELIGIBILITY_KEYS = [
  "activeInventory",
  "spreadsheetFeed",
  "whatsappContact",
  "inventoryUpdates",
  "leadFeedback",
];

const DEALER_FREE_LEADS_INCLUDED_KEYS = [
  "freeLeads",
  "verifiedProfile",
  "searchVisibility",
  "whatsappContext",
  "pilotSupport",
];

const DEALER_FREE_LEADS_RESPONSIBILITY_KEYS = [
  "weeklyInventory",
  "respondLeads",
  "confirmContact",
  "reportOutcomes",
];

const DEALER_FREE_LEADS_TIMELINE_KEYS = ["week1", "week2", "pilot", "day90"];

const DEALER_FREE_LEADS_FAQ_KEYS = ["pilotCost", "afterNinety", "integration"];

export function getBuyerChecklistHubCopy(t) {
  return {
    eyebrow: t("automotive.buyerChecklistHub.eyebrow"),
    title: t("automotive.buyerChecklistHub.title"),
    intro: t("automotive.buyerChecklistHub.intro"),
    loadError: t("automotive.buyerChecklistHub.loadError"),
    disclaimer: t("automotive.buyerChecklistHub.disclaimer"),
    progressCompleted: (checked, total, percent) =>
      t("automotive.buyerChecklistHub.progressCompleted", { checked, total, percent }),
    progressEmpty: t("automotive.buyerChecklistHub.progressEmpty"),
    print: t("automotive.buyerChecklistHub.print"),
    share: t("automotive.buyerChecklistHub.share"),
    save: t("automotive.buyerChecklistHub.save"),
    contextEyebrow: t("automotive.buyerChecklistHub.contextEyebrow"),
    backToVehicle: t("automotive.buyerChecklistHub.backToVehicle"),
    viewDetailedGuide: t("automotive.buyerChecklistHub.viewDetailedGuide"),
    searchVehicles: t("automotive.buyerChecklistHub.searchVehicles"),
    savedProgress: t("automotive.buyerChecklistHub.savedProgress"),
    footerNote: t("automotive.buyerChecklistHub.footerNote"),
  };
}

export function getDealerFreeLeadsCopy(t) {
  return {
    eyebrow: t("automotive.dealerFreeLeads.eyebrow"),
    title: t("automotive.dealerFreeLeads.title"),
    intro: t("automotive.dealerFreeLeads.intro"),
    requestAccess: t("automotive.dealerFreeLeads.requestAccess"),
    overviewHeading: t("automotive.dealerFreeLeads.overviewHeading"),
    overviewIntro: t("automotive.dealerFreeLeads.overviewIntro"),
    eligibility: {
      heading: t("automotive.dealerFreeLeads.eligibility.heading"),
      items: DEALER_FREE_LEADS_ELIGIBILITY_KEYS.map((key) =>
        t(`automotive.dealerFreeLeads.eligibility.items.${key}`)
      ),
    },
    included: {
      heading: t("automotive.dealerFreeLeads.included.heading"),
      items: DEALER_FREE_LEADS_INCLUDED_KEYS.map((key) =>
        t(`automotive.dealerFreeLeads.included.items.${key}`)
      ),
    },
    responsibilities: {
      heading: t("automotive.dealerFreeLeads.responsibilities.heading"),
      items: DEALER_FREE_LEADS_RESPONSIBILITY_KEYS.map((key) =>
        t(`automotive.dealerFreeLeads.responsibilities.items.${key}`)
      ),
    },
    timeline: {
      heading: t("automotive.dealerFreeLeads.timeline.heading"),
      steps: DEALER_FREE_LEADS_TIMELINE_KEYS.map((key) => ({
        title: t(`automotive.dealerFreeLeads.timeline.steps.${key}.title`),
        text: t(`automotive.dealerFreeLeads.timeline.steps.${key}.text`),
      })),
    },
    faq: {
      heading: t("automotive.dealerFreeLeads.faq.heading"),
      items: DEALER_FREE_LEADS_FAQ_KEYS.map((key) => ({
        q: t(`automotive.dealerFreeLeads.faq.items.${key}.q`),
        a: t(`automotive.dealerFreeLeads.faq.items.${key}.a`),
      })),
    },
    cta: {
      heading: t("automotive.dealerFreeLeads.cta.heading"),
      body: t("automotive.dealerFreeLeads.cta.body"),
    },
    footerTagline: (siteName) =>
      t("automotive.dealerLanding.shell.footerTagline", { siteName }),
  };
}

export function getDealerProfilePageCopy(t) {
  const landingCopy = getDealerLandingPageCopy(t);
  const partnershipCopy = getDealerDirectoryPartnershipAdCopy(t);

  return {
    dealerLabel: t("automotive.dealerProfile.dealerLabel"),
    loading: t("automotive.dealerProfile.loading"),
    loadingHeroAria: t("automotive.dealerProfile.loadingHeroAria"),
    heroAria: t("automotive.dealerProfile.heroAria"),
    backToSearch: t("automotive.dealerProfile.backToSearch"),
    inventoryLabel: t("automotive.dealerProfile.inventoryLabel"),
    inventoryUnknown: t("automotive.dealerProfile.inventoryUnknown"),
    inventoryEmpty: t("automotive.dealerProfile.inventoryEmpty"),
    inventoryOne: t("automotive.dealerProfile.inventoryOne"),
    inventoryMany: (count) => t("automotive.dealerProfile.inventoryMany", { count }),
    dealerNameFallback: t("automotive.dealerProfile.dealerNameFallback"),
    locationFallback: t("automotive.dealerProfile.locationFallback"),
    verificationUnavailable: t("automotive.dealerProfile.verificationUnavailable"),
    contactDealer: t("automotive.dealerProfile.contactDealer"),
    contactWhatsapp: t("automotive.dealerProfile.contactWhatsapp"),
    callDealer: t("automotive.dealerProfile.callDealer"),
    searchCta: t("automotive.dealerProfile.searchCta"),
    directoryCta: t("automotive.dealerProfile.directoryCta"),
    retry: t("automotive.dealerProfile.retry"),
    viewInventory: t("automotive.dealerProfile.viewInventory"),
    logoAlt: (name) =>
      name
        ? t("automotive.dealerProfile.logoAlt", { name })
        : t("automotive.dealerProfile.logoAltFallback"),
    exploreOtherDealers: t("automotive.dealerDirectory.exploreOtherDealers"),
    publicNoInventory: t("automotive.dealerProfile.states.publicNoInventory"),
    publicNoInventoryContactNote: t("automotive.dealerProfile.states.publicNoInventoryContactNote"),
    publicContactLimited: t("automotive.dealerProfile.states.publicContactLimited"),
    inventory: {
      heading: t("automotive.dealerProfile.inventory.heading"),
      loading: t("automotive.dealerProfile.inventory.loading"),
      errorTitle: t("automotive.dealerProfile.inventory.errorTitle"),
      errorBody: t("automotive.dealerProfile.inventory.errorBody"),
      emptyTitle: t("automotive.dealerProfile.inventory.emptyTitle"),
      emptyBody: t("automotive.dealerProfile.inventory.emptyBody"),
      retry: t("automotive.dealerProfile.inventory.retry"),
      exploreOtherDealers: t("automotive.dealerProfile.inventory.exploreOtherDealers"),
      subheading: (dealerName) =>
        t("automotive.dealerProfile.inventory.subheading", {
          dealerName: dealerName || t("automotive.dealerProfile.dealerNameFallback"),
        }),
      countOne: t("automotive.dealerProfile.inventory.countOne"),
      countMany: (count) => t("automotive.dealerProfile.inventory.countMany", { count }),
      loadMore: t("automotive.dealerProfile.inventory.loadMore"),
      searchCity: (city) => t("automotive.dealerProfile.inventory.searchCity", { city }),
      searchMore: t("automotive.dealerProfile.inventory.searchMore"),
      filteredEmpty: t("automotive.dealerProfile.inventory.filteredEmpty"),
      filteredCountOne: t("automotive.dealerProfile.inventory.filteredCountOne"),
      filteredCountMany: (count) =>
        t("automotive.dealerProfile.inventory.filteredCountMany", { count }),
      filterInventory: t("automotive.dealerProfile.inventory.filterInventory"),
      filter: t("automotive.dealerProfile.inventory.filter"),
      sort: t("automotive.dealerProfile.inventory.sort"),
      close: t("automotive.dealerProfile.inventory.close"),
      applyFilters: t("automotive.dealerProfile.inventory.applyFilters"),
      appliedFilters: t("automotive.dealerProfile.inventory.appliedFilters"),
      clearFilters: t("automotive.dealerProfile.inventory.clearFilters"),
      showResults: t("automotive.dealerProfile.inventory.showResults"),
      viewAll: t("automotive.dealerProfile.inventory.viewAll"),
      yearMax: t("automotive.dealerProfile.inventory.yearMax"),
      yearPlaceholder: t("automotive.dealerProfile.inventory.yearPlaceholder"),
      sortOptions: getDealerInventorySortOptions(t),
      filters: getDealerInventoryFilterCopy(t),
    },
    heroTrustNote: (dealerTrust) =>
      dealerTrust?.isVerified
        ? t("automotive.dealerProfile.heroTrustNote.verified")
        : t("automotive.dealerProfile.heroTrustNote.default"),
    trustSignalsAria: t("automotive.dealerProfile.trustSignalsAria"),
    mobileContactAria: t("automotive.dealerProfile.mobileContactAria"),
    trust: {
      loadingAria: t("automotive.dealerProfile.trust.loadingAria"),
      heading: t("automotive.dealerProfile.trust.heading"),
      checklistCta: t("automotive.dealerProfile.trust.checklistCta"),
      signalsAria: t("automotive.dealerProfile.trust.signalsAria"),
      dealerStatus: t("automotive.dealerProfile.trust.dealerStatus"),
      whatReviewed: t("automotive.dealerProfile.trust.whatReviewed"),
      beforeVisit: t("automotive.dealerProfile.trust.beforeVisit"),
      contactInventory: t("automotive.dealerProfile.trust.contactInventory"),
      checklistItems: [
        t("automotive.dealerProfile.trust.checklistItems.availability"),
        t("automotive.dealerProfile.trust.checklistItems.price"),
        t("automotive.dealerProfile.trust.checklistItems.soat"),
        t("automotive.dealerProfile.trust.checklistItems.history"),
        t("automotive.dealerProfile.trust.checklistItems.inspection"),
        t("automotive.dealerProfile.trust.checklistItems.transfer"),
      ],
    },
    locationTitle: t("automotive.dealerProfile.sidebar.mapTitle"),
    sidebarAria: t("automotive.dealerProfile.sidebar.aria"),
    sidebar: {
      mapTitle: t("automotive.dealerProfile.sidebar.mapTitle"),
      mapSubtitle: (dealerName) =>
        t("automotive.dealerProfile.sidebar.mapSubtitle", { dealerName }),
      locationCountOne: t("automotive.dealerProfile.sidebar.locationCountOne"),
      locationCountMany: (count) =>
        t("automotive.dealerProfile.sidebar.locationCountMany", { count }),
      mapNoLocations: t("automotive.dealerDirectory.page.mapNoLocations"),
      mapUnavailable: t("automotive.dealerDirectory.page.mapUnavailable"),
      dealerNameFallback: t("automotive.dealerProfile.dealerNameFallback"),
      promo: {
        eyebrow: partnershipCopy.eyebrow,
        title: landingCopy.title,
        body: landingCopy.intro,
        foundingTitle: landingCopy.foundingOfferTitle,
        foundingBody: landingCopy.foundingOfferBody,
        bullets: landingCopy.valueBullets,
        cta: landingCopy.requestAccess,
        secondaryCta: landingCopy.learnFreeLeads,
      },
    },
  };
}

function resolveDealerVerificationStatusKey(dealerTrust) {
  const status = dealerTrust?.verificationStatus;
  if (status && ["verified", "pending", "unverified", "unknown"].includes(status)) return status;
  return "unknown";
}

export function getDealerProfileVerificationLabel(t, status) {
  const key = status && ["verified", "pending", "unverified", "rejected", "suspended", "unknown"].includes(status)
    ? status
    : "unknown";
  return t(`automotive.dealerProfile.verificationLabels.${key}`);
}

export function getDealerProfileContactDisabledLabel(t, reason) {
  const key = reason || "unknown";
  const translated = t(`automotive.dealerProfile.contactDisabled.${key}`);
  if (translated !== `automotive.dealerProfile.contactDisabled.${key}`) return translated;
  return t("automotive.dealerProfile.contactDisabled.unknown");
}

export function getDealerProfileLastInventoryLabel(t, locale, isoDate) {
  if (!isoDate) return null;
  const ts = new Date(isoDate).getTime();
  if (Number.isNaN(ts)) return null;
  const days = Math.round((Date.now() - ts) / 86_400_000);
  if (days <= 2) return t("automotive.dealerProfile.lastInventoryRecent");
  const dateLabel = new Date(isoDate).toLocaleDateString(locale || "es-CO");
  return t("automotive.dealerProfile.lastInventoryUpdated", { date: dateLabel });
}

export function getDealerProfileTrustPanelVerificationExplanation(t, dealerTrust) {
  const key = resolveDealerVerificationStatusKey(dealerTrust);
  return t(`automotive.dealerProfile.trust.verificationExplanations.${key}`);
}

export function getDealerProfileTrustPanelWhatReviewedCopy(t, dealerTrust) {
  const key = resolveDealerVerificationStatusKey(dealerTrust);
  return t(`automotive.dealerProfile.trust.whatReviewedCopy.${key}`);
}

export function getDealerProfileTrustPanelInventoryMessage(t, inventorySummary, publicState) {
  if (publicState?.state === DEALER_PROFILE_PAGE_STATE.PUBLIC_NO_INVENTORY) {
    return t("automotive.dealerProfile.trust.inventoryMessages.empty");
  }
  const count = inventorySummary?.activeInventoryCount;
  if (count == null || Number.isNaN(Number(count))) {
    return t("automotive.dealerProfile.trust.inventoryMessages.unknown");
  }
  const value = Number(count);
  if (value <= 0) return t("automotive.dealerProfile.trust.inventoryMessages.empty");
  if (value === 1) return t("automotive.dealerProfile.trust.inventoryMessages.one");
  return t("automotive.dealerProfile.trust.inventoryMessages.many", { count: value });
}

export function getDealerProfileTrustPanelContactMessage(t, dealerContact, publicState) {
  const contactLimited =
    publicState?.state === DEALER_PROFILE_PAGE_STATE.PUBLIC_CONTACT_LIMITED ||
    !dealerContact?.canContact;
  return contactLimited
    ? t("automotive.dealerProfile.trust.contactMessages.unavailable")
    : t("automotive.dealerProfile.trust.contactMessages.available");
}

export function buildDealerProfileTrustSignals(t, { dealerTrust, dealerContact, inventorySummary } = {}) {
  const locationLabel = dealerTrust?.locationLabel;
  const hasLocation =
    locationLabel && !isDealerProfileLocationFallbackLabel(locationLabel);

  let contactValue = t("automotive.dealerProfile.trust.signalFallbacks.contact");
  if (dealerContact?.canContact && dealerContact?.whatsappHref) {
    contactValue = t("automotive.dealerProfile.trust.signalFallbacks.contactWhatsapp");
  } else if (dealerContact?.canContact && dealerContact?.phoneHref) {
    contactValue = t("automotive.dealerProfile.trust.signalFallbacks.contactPhone");
  }

  const activityValue =
    inventorySummary?.lastUpdatedLabel ||
    (dealerTrust?.responseTimeLabel
      ? t("automotive.dealerProfile.responseTimePrefix", { label: dealerTrust.responseTimeLabel })
      : null) ||
    t("automotive.dealerProfile.trust.signalFallbacks.activity");

  return [
    {
      key: "verification",
      label: t("automotive.dealerProfile.trust.signalLabels.verification"),
      value: dealerTrust?.verificationLabel || t("automotive.dealerProfile.verificationUnavailable"),
    },
    {
      key: "inventory",
      label: t("automotive.dealerProfile.trust.signalLabels.inventory"),
      value:
        inventorySummary?.inventoryAvailabilityLabel ||
        t("automotive.dealerProfile.trust.signalFallbacks.inventory"),
    },
    {
      key: "contact",
      label: t("automotive.dealerProfile.trust.signalLabels.contact"),
      value: contactValue,
    },
    {
      key: "location",
      label: t("automotive.dealerProfile.trust.signalLabels.location"),
      value: hasLocation
        ? locationLabel
        : t("automotive.dealerProfile.trust.signalFallbacks.location"),
    },
    {
      key: "activity",
      label: t("automotive.dealerProfile.trust.signalLabels.activity"),
      value: activityValue,
    },
  ];
}

export function getDealerInventorySortOptions(t) {
  return [
    { value: VEHICLE_SEARCH_SORT.RECOMMENDED, label: t("automotive.search.sortRecommended") },
    { value: VEHICLE_SEARCH_SORT.NEWEST, label: t("automotive.search.sortNewest") },
    { value: VEHICLE_SEARCH_SORT.RECENTLY_CONFIRMED, label: t("automotive.search.sortRecentlyConfirmed") },
    { value: VEHICLE_SEARCH_SORT.LOWEST_PRICE, label: t("automotive.search.sortLowestPrice") },
    { value: VEHICLE_SEARCH_SORT.HIGHEST_PRICE, label: t("automotive.search.sortHighestPrice") },
    { value: VEHICLE_SEARCH_SORT.LOWEST_MILEAGE, label: t("automotive.search.sortLowestMileage") },
    { value: VEHICLE_SEARCH_SORT.NEWEST_YEAR, label: t("automotive.search.sortNewestYear") },
    { value: VEHICLE_SEARCH_SORT.BEST_VALUE, label: t("automotive.search.sortBestValue") },
  ];
}

export function getDealerInventoryFilterCopy(t, options = {}) {
  const searchCopy = getSearchUiCopy(t, options);
  const stack = searchCopy.filterStack;
  const cardCopy = getVehicleCardCopy(t);
  const unknown = getUnknownAttributeLabel(t);

  const bodyTypeLabel = (value) => {
    const label = cardCopy.listingBody(value);
    return label !== unknown ? label : value;
  };

  const transmissionLabel = (value) => {
    const label = cardCopy.listingTransmission(value);
    return label !== unknown ? label : value;
  };

  const fuelTypeLabel = (value) => stack.fuelLabels?.[value] || value;

  return {
    filterInventory: t("automotive.dealerProfile.inventory.filterInventory"),
    filter: t("automotive.dealerProfile.inventory.filter"),
    clearFilters: t("automotive.dealerProfile.inventory.clearFilters"),
    showResults: t("automotive.dealerProfile.inventory.showResults"),
    appliedFilters: t("automotive.dealerProfile.inventory.appliedFilters"),
    make: stack.make,
    model: stack.model,
    priceMin: stack.minPrice,
    priceMax: stack.maxPrice,
    yearMin: stack.yearFrom,
    yearMax: t("automotive.dealerProfile.inventory.yearMax"),
    yearPlaceholder: t("automotive.dealerProfile.inventory.yearPlaceholder"),
    mileageMax: stack.mileageMax,
    bodyType: stack.bodyType,
    transmission: stack.transmission,
    fuelType: stack.fuelType,
    fairPrice: stack.fairPrice,
    riskLevel: stack.riskLevel,
    hasPhotos: stack.hasPhotos,
    allMakes: stack.allMakes,
    allModels: stack.allModels,
    allOptions: stack.allOptions,
    allBodyTypes: stack.allBodyTypes,
    allTransmissions: stack.allTransmissions,
    minPriceOptions: stack.minPriceOptions,
    maxPriceOptions: stack.maxPriceOptions,
    yearMinOptions: stack.yearMinOptions,
    mileageOptions: stack.mileageOptions,
    fairPriceLabels: stack.fairPriceLabels,
    riskLabels: stack.riskLabels,
    bodyTypeLabel,
    transmissionLabel,
    fuelTypeLabel,
    chipCopy: searchCopy.filterChips,
    searchCopy,
  };
}

export function localizeDealerProfileTrustViewModel(t, vm = {}) {
  if (!vm) return vm;
  return {
    ...vm,
    verificationLabel: getDealerProfileVerificationLabel(t, vm.verificationStatus),
    verificationDescription: getDealerProfileVerificationLabel(t, vm.verificationStatus),
    contactDisabledLabel: getDealerProfileContactDisabledLabel(t, vm.contactDisabledReason),
    locationLabel: isDealerProfileLocationFallbackLabel(vm.locationLabel)
      ? t("automotive.dealerProfile.locationFallback")
      : vm.locationLabel,
  };
}

export function localizeDealerProfileContactViewModel(t, vm = {}) {
  if (!vm) return vm;
  return {
    ...vm,
    locationLabel: isDealerProfileLocationFallbackLabel(vm.locationLabel)
      ? t("automotive.dealerProfile.locationFallback")
      : vm.locationLabel,
    verificationLabel: getDealerProfileVerificationLabel(t, vm.verificationStatus),
    primaryCtaLabel: vm.canContact
      ? t("automotive.dealerProfile.contactWhatsapp")
      : t("automotive.dealerProfile.contactDisabled.unknown"),
    secondaryCtaLabel: vm.phoneHref ? t("automotive.dealerProfile.callDealer") : null,
    contactDisabledLabel: getDealerProfileContactDisabledLabel(t, vm.contactDisabledReason),
    profileCtaLabel: t("automotive.home.viewDealerProfile"),
  };
}

export function localizeDealerProfileInventorySummary(t, locale, summary = {}, dealer = null) {
  if (!summary) return summary;
  const inventoryAvailabilityLabel = getDealerProfileInventoryLabel(t, summary);
  return {
    ...summary,
    inventoryAvailabilityLabel,
    inventoryCountLabel: inventoryAvailabilityLabel,
    lastUpdatedLabel: getDealerProfileLastInventoryLabel(
      t,
      locale,
      summary.lastInventoryUpdatedLabel || dealer?.lastInventoryUpdatedAt
    ),
  };
}

export function getDealerProfileInventoryLabel(t, summary = {}) {
  const count = summary.activeInventoryCount ?? summary.inventoryCount;
  if (count == null || Number.isNaN(Number(count))) {
    return t("automotive.dealerProfile.inventoryUnknown");
  }
  const value = Number(count);
  if (value <= 0) return t("automotive.dealerProfile.inventoryEmpty");
  if (value === 1) return t("automotive.dealerProfile.inventoryOne");
  return t("automotive.dealerProfile.inventoryMany", { count: value });
}

export function getDealerProfileStateCopy(t, presentation, publicState) {
  const stateKey = publicState?.state;
  const stateMessages = {
    hidden: t("automotive.dealerProfile.states.hidden"),
    inactive: t("automotive.dealerProfile.states.inactive"),
    suspended: t("automotive.dealerProfile.states.suspended"),
    rejected: t("automotive.dealerProfile.states.rejected"),
    deleted: t("automotive.dealerProfile.states.deleted"),
    unknown: t("automotive.dealerProfile.states.unknown"),
  };

  if (presentation?.view === "error") {
    return {
      title: t("automotive.dealerProfile.states.error"),
      body: t("automotive.dealerProfile.states.errorBody"),
    };
  }

  if (presentation?.view === "not_found") {
    return {
      title: t("automotive.dealerProfile.states.notFound"),
      body: t("automotive.dealerProfile.states.notFoundBody"),
    };
  }

  if (presentation?.view === "unavailable") {
    return {
      title: stateMessages[stateKey] || t("automotive.dealerProfile.states.unknown"),
      body: t("automotive.dealerProfile.states.unavailableBody"),
    };
  }

  return { title: "", body: "" };
}

export function getDealerProfileBreadcrumbs(t, { homePath, searchPath, directoryPath, dealerName }) {
  return [
    { label: t("automotive.dealerDirectory.breadcrumbHome"), href: homePath },
    { label: t("automotive.dealerDirectory.breadcrumbUsedCars"), href: searchPath },
    { label: t("automotive.dealerProfile.breadcrumbDealers"), href: directoryPath },
    { label: dealerName || t("automotive.dealerProfile.dealerNameFallback") },
  ];
}

export function getDetailUiCopy(t) {
  return {
    financeTitle: t("automotive.detail.financeTitle"),
    financeDisclaimer: t("automotive.detail.financeDisclaimer"),
    downPaymentLabel: (pct) => t("automotive.detail.downPaymentLabel", { pct }),
    downPaymentRow: t("automotive.detail.downPaymentRow"),
    financedAmount: t("automotive.detail.financedAmount"),
    estimatedMonthly: t("automotive.detail.estimatedMonthly"),
    marketRange: t("automotive.detail.marketRange"),
    reportProblem: t("automotive.detail.reportProblem"),
    safeBuyingGuide: t("automotive.detail.safeBuyingGuide"),
    footerTagline: t("automotive.detail.footerTagline"),
    sectionNavAria: t("automotive.detail.sectionNavAria"),
    breadcrumbHome: t("automotive.detail.breadcrumbHome"),
    breadcrumbUsedCars: t("automotive.detail.breadcrumbUsedCars"),
    breadcrumbDetail: t("automotive.detail.breadcrumbDetail"),
    breadcrumbNavAria: t("automotive.detail.breadcrumbNavAria"),
    heroEyebrowVerified: t("automotive.detail.heroEyebrowVerified"),
    heroPriceLabel: t("automotive.detail.heroPriceLabel"),
    heroViewPhotos: t("automotive.detail.heroViewPhotos"),
    heroContact: t("automotive.detail.heroContact"),
    featured: t("automotive.detail.featured"),
    summaryAria: t("automotive.detail.summaryAria"),
    loadingPrimary: t("automotive.detail.loadingPrimary"),
    loadingSecondary: t("automotive.detail.loadingSecondary"),
    errorTitle: t("automotive.detail.errorTitle"),
    errorBody: t("automotive.detail.errorBody"),
    retry: t("automotive.detail.retry"),
    backToSearch: t("automotive.detail.backToSearch"),
    notFoundTitle: t("automotive.detail.notFoundTitle"),
    notFoundBody: t("automotive.detail.notFoundBody"),
    viewAvailable: t("automotive.detail.viewAvailable"),
    unavailableTitle: t("automotive.detail.unavailableTitle"),
    unavailableBody: t("automotive.detail.unavailableBody"),
    unavailableFallbackTitle: t("automotive.detail.unavailableFallbackTitle"),
    viewSimilar: t("automotive.detail.viewSimilar"),
    specLabels: {
      year: t("automotive.year"),
      mileage: t("automotive.mileage"),
      transmission: t("automotive.transmission"),
      fuelType: t("automotive.fuelType"),
      bodyType: t("automotive.search.filterStackBodyType"),
      location: t("automotive.location"),
      make: t("automotive.make"),
      model: t("automotive.model"),
      version: t("automotive.detail.layout.version"),
      engine: t("automotive.detail.layout.engine"),
      drivetrain: t("automotive.detail.layout.drivetrain"),
      doors: t("automotive.detail.layout.doors"),
      unknownAttribute: t("automotive.attributeUnknown"),
      type: t("automotive.detail.layout.type"),
      neighborhood: t("automotive.detail.layout.neighborhood"),
      lastUpdated: t("automotive.detail.layout.lastUpdated"),
      availabilityStatus: t("automotive.detail.layout.availabilityStatus"),
    },
    layout: {
      specsHeading: t("automotive.detail.layout.specsHeading"),
      availabilityHeading: t("automotive.detail.layout.availabilityHeading"),
      availabilityNote: t("automotive.detail.layout.availabilityNote"),
      availabilityFallback: t("automotive.detail.layout.availabilityFallback"),
      fairPriceHeading: t("automotive.detail.layout.fairPriceHeading"),
      fairPriceNoData: t("automotive.detail.layout.fairPriceNoData"),
      fairPriceDisclaimer: t("automotive.detail.layout.fairPriceDisclaimer"),
      riskHeading: t("automotive.detail.layout.riskHeading"),
      riskNoData: t("automotive.detail.layout.riskNoData"),
      riskLowCopy: t("automotive.detail.layout.riskLowCopy"),
      riskDisclaimer: t("automotive.detail.layout.riskDisclaimer"),
      checklistHeading: t("automotive.detail.layout.checklistHeading"),
      checklistSubheading: t("automotive.detail.layout.checklistSubheading"),
      checklistCta: t("automotive.detail.layout.checklistCta"),
      share: t("automotive.detail.layout.share"),
      copyLink: t("automotive.detail.layout.copyLink"),
      linkCopied: t("automotive.detail.layout.linkCopied"),
      galleryEmpty: t("automotive.detail.layout.galleryEmpty"),
      galleryAria: t("automotive.detail.layout.galleryAria"),
      photoPrevious: t("automotive.detail.layout.photoPrevious"),
      photoNext: t("automotive.detail.layout.photoNext"),
      thumbnailsAria: t("automotive.detail.layout.thumbnailsAria"),
      viewPhoto: (index, total) =>
        t("automotive.detail.layout.viewPhoto", { index, total }),
      openGallery: (current, total) =>
        t("automotive.detail.layout.openGallery", { current, total }),
      expandGallery: t("automotive.detail.layout.expandGallery"),
      closeGallery: t("automotive.detail.layout.closeGallery"),
      imageUnavailable: t("automotive.detail.layout.imageUnavailable"),
      dealerLabel: t("automotive.detail.layout.dealerLabel"),
      contactWhatsapp: t("automotive.detail.layout.contactWhatsapp"),
      contactSeller: t("automotive.detail.layout.contactSeller"),
      requestInfo: t("automotive.detail.layout.requestInfo"),
      callDealer: t("automotive.detail.layout.callDealer"),
      viewProfile: t("automotive.detail.layout.viewProfile"),
      contactUnavailable: t("automotive.detail.layout.contactUnavailable"),
      contactUnavailableNow: t("automotive.detail.layout.contactUnavailableNow"),
      listingUnavailableContact: t("automotive.detail.layout.listingUnavailableContact"),
      mobileContact: t("automotive.detail.layout.mobileContact"),
      mobileSimilar: t("automotive.detail.layout.mobileSimilar"),
      trustStripAria: t("automotive.detail.layout.trustStripAria"),
      dealerCardAria: t("automotive.detail.layout.dealerCardAria"),
      mobileBarAria: t("automotive.detail.layout.mobileBarAria"),
      plateEndsIn: (digit) => t("automotive.detail.layout.plateEndsIn", { digit }),
      trustVerified: t("automotive.detail.layout.trustVerified"),
      trustFairPrice: t("automotive.detail.layout.trustFairPrice"),
      trustChecklist: t("automotive.detail.layout.trustChecklist"),
      trustRisk: t("automotive.detail.layout.trustRisk"),
      compareSimilar: t("automotive.detail.layout.compareSimilar"),
      howCalculated: t("automotive.detail.layout.howCalculated"),
      fairPriceMethodology: t("automotive.detail.layout.fairPriceMethodology"),
      fairPriceEstimateDisclaimer: t("automotive.detail.layout.fairPriceEstimateDisclaimer"),
      riskMethodology: t("automotive.detail.layout.riskMethodology"),
      suggestedQuestion: t("automotive.detail.layout.suggestedQuestion"),
      beforeContactQuestions: t("automotive.detail.layout.beforeContactQuestions"),
      leadFormIntro: t("automotive.detail.layout.leadFormIntro"),
      nameLabel: t("automotive.detail.layout.nameLabel"),
      namePlaceholder: t("automotive.detail.layout.namePlaceholder"),
      submitRequest: t("automotive.detail.layout.submitRequest"),
      browseDirectory: t("automotive.detail.layout.browseDirectory"),
      shareDefaultTitle: t("automotive.detail.layout.shareDefaultTitle"),
      navMainAria: t("automotive.detail.layout.navMainAria"),
      navFooterAria: t("automotive.detail.layout.navFooterAria"),
    },
  };
}

function resolveCatalogLabel(t, key, fallback) {
  if (!t) return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

export function getRecommendationDetailCopy(t) {
  return {
    heading: t("automotive.detail.recommendations.heading"),
    subheading: t("automotive.detail.recommendations.subheading"),
    viewMore: t("automotive.detail.recommendations.viewMore"),
    unavailableHeading: t("automotive.detail.recommendations.unavailableHeading"),
    unavailableSubheading: t("automotive.detail.recommendations.unavailableSubheading"),
    viewAvailable: t("automotive.detail.recommendations.viewAvailable"),
    loadError: t("automotive.detail.recommendations.loadError"),
    emptyMessage: t("automotive.detail.recommendations.emptyMessage"),
    emptyHidden: true,
  };
}

export function getContactChecklistReminderText(level, dealerVm, t) {
  if (!t) return null;
  if (["medium", "high"].includes(level)) {
    return t("automotive.detail.contactReminders.riskReview");
  }
  if (dealerVm && !dealerVm.isVerified) {
    return t("automotive.detail.contactReminders.unverifiedDealer");
  }
  return t("automotive.detail.contactReminders.beforeVisit");
}

export function localizeChecklistItem(item, t) {
  if (!item || !t) return item;
  const base = `automotive.detail.checklistItems.${item.id}`;
  const shortTitle = resolveCatalogLabel(t, `${base}.shortTitle`, null);
  if (!shortTitle) return item;
  return {
    ...item,
    shortTitle,
    title: resolveCatalogLabel(t, `${base}.title`, item.title),
    question: resolveCatalogLabel(t, `${base}.question`, item.question || item.description),
    whyItMatters: resolveCatalogLabel(t, `${base}.whyItMatters`, item.whyItMatters),
    category: resolveCatalogLabel(
      t,
      `automotive.detail.checklistCategories.${item.category}`,
      item.category
    ),
  };
}

export function getChecklistCardCopy(t) {
  return {
    priorityHigh: t("automotive.detail.checklistUi.priorityHigh"),
    priorityMedium: t("automotive.detail.checklistUi.priorityMedium"),
    priorityLow: t("automotive.detail.checklistUi.priorityLow"),
    whyItMatters: t("automotive.detail.checklistUi.whyItMatters"),
    hideDetail: t("automotive.detail.checklistUi.hideDetail"),
    viewGuide: t("automotive.detail.checklistUi.viewGuide"),
    compareSimilar: t("automotive.detail.checklistUi.compareSimilar"),
    viewSimilar: t("automotive.detail.checklistUi.viewSimilar"),
  };
}

export function getSearchUiCopy(t, { formatMoney, displayCurrency } = {}) {
  const formatAmount = (amountCop) => {
    if (amountCop == null || Number.isNaN(Number(amountCop))) return "";
    if (typeof formatMoney === "function") {
      return formatMoney(Number(amountCop), { displayCurrency, showApproximation: false });
    }
    return formatTemplatePrice(amountCop, displayCurrency || "COP", { formatMoney });
  };

  const budgetOptions = buildSearchBudgetOptions({ t, formatAmount });
  const minPriceOptions = buildSearchMinPriceOptions({ t, formatAmount });
  const maxPriceOptions = buildSearchMaxPriceOptions({ t, formatAmount });
  const formatPriceChip = (cop) => {
    if (cop == null) return null;
    const formatted = formatAmount(cop);
    return formatted || null;
  };

  return {
    loading: {
      primary: t("automotive.search.loadingPrimary"),
      secondary: t("automotive.search.loadingSecondary"),
    },
    error: {
      title: t("automotive.search.errorTitle"),
      body: t("automotive.search.errorBody"),
      retry: t("common.retry"),
    },
    empty: {
      title: t("automotive.search.emptyTitle"),
      body: t("automotive.search.emptyBody"),
      cityHint: t("automotive.search.emptyCityHint"),
      resetFilters: t("automotive.search.resetFilters"),
      viewAllVehicles: t("automotive.search.viewAllVehicles"),
      checklistCta: t("automotive.search.checklistCta"),
      quizCta: t("automotive.search.quizCta"),
      dealerDirectoryCta: t("automotive.search.dealerDirectoryCta"),
    },
    resultsForSearch: t("automotive.search.resultsForSearch"),
    dealerResponseTime: t("automotive.search.dealerResponseTime"),
    verifiedListings: t("automotive.search.verifiedListings"),
    searchPlaceholder: t("automotive.search.searchPlaceholder"),
    leadSuccessWhatsapp: t("automotive.search.leadSuccessWhatsapp"),
    seoTitle: t("automotive.search.seoTitle"),
    refreshResults: t("automotive.search.refreshResults"),
    confirmedThisWeek: t("automotive.search.confirmedThisWeek"),
    buyingChecklist: t("automotive.search.buyingChecklist"),
    submitSearch: t("automotive.search.submitSearch"),
    form: {
      searchLabel: t("automotive.search.formSearchLabel"),
      cityLabel: t("automotive.search.formCityLabel"),
      budgetLabel: t("automotive.search.formBudgetLabel"),
      typeLabel: t("automotive.search.filterStackBodyType"),
      fuelLabel: t("automotive.search.formFuelLabel"),
      transmissionLabel: t("automotive.search.formTransmissionLabel"),
      allColombia: t("automotive.search.allColombia"),
      allTypes: t("automotive.search.allTypes"),
      allTransmissions: t("automotive.search.allTransmissions"),
      bodyAutomatic: t("automotive.search.bodyAutomatic"),
      bodyManual: t("automotive.search.bodyManual"),
      bodySedan: t("automotive.search.bodySedan"),
    },
    filters: {
      title: t("automotive.search.filtersTitle"),
      clear: t("automotive.search.filtersClear"),
      mobile: t("automotive.search.filtersMobile"),
      getRecommendations: t("automotive.search.getRecommendations"),
      activeHeading: t("automotive.search.activeFiltersHeading"),
      clearAll: t("automotive.search.clearAllFilters"),
      removeAria: t("automotive.search.removeFilterAria"),
    },
    featured: {
      title: t("automotive.search.featuredTitle"),
      hint: t("automotive.search.featuredHint"),
    },
    pagination: {
      loadMore: t("automotive.search.loadMore"),
      end: t("automotive.search.paginationEnd"),
      previous: t("automotive.search.paginationPrevious"),
      next: t("automotive.search.paginationNext"),
      pageOf: (page, totalPages) =>
        t("automotive.search.paginationPageOf", { page, totalPages }),
      showing: (visible, total) =>
        total != null && total > 0
          ? t("automotive.search.showingCount", { visible, total })
          : null,
    },
    leadStrip: {
      title: t("automotive.search.leadStripTitle"),
      body: (siteName) => t("automotive.search.leadStripBody", { siteName }),
      cta: t("automotive.search.leadStripCta"),
    },
    footerTagline: t("automotive.search.footerTagline"),
    close: t("automotive.search.close"),
    devInventoryNotice: t("automotive.search.devInventoryNotice"),
    unmappedHint: t("automotive.search.unmappedHint"),
    budgetOptions,
    formatPriceChip,
    sortOptions: [
      { value: VEHICLE_SEARCH_SORT.RECOMMENDED, label: t("automotive.search.sortRecommended") },
      { value: VEHICLE_SEARCH_SORT.NEWEST, label: t("automotive.search.sortNewest") },
      { value: VEHICLE_SEARCH_SORT.LOWEST_PRICE, label: t("automotive.search.sortLowestPrice") },
      { value: VEHICLE_SEARCH_SORT.HIGHEST_PRICE, label: t("automotive.search.sortHighestPrice") },
      { value: VEHICLE_SEARCH_SORT.LOWEST_MILEAGE, label: t("automotive.search.sortLowestMileage") },
      { value: VEHICLE_SEARCH_SORT.NEWEST_YEAR, label: t("automotive.search.sortNewestYear") },
      { value: VEHICLE_SEARCH_SORT.BEST_VALUE, label: t("automotive.search.sortBestValue") },
      {
        value: VEHICLE_SEARCH_SORT.VERIFIED_DEALERS_FIRST,
        label: t("automotive.search.sortVerifiedDealers"),
      },
      {
        value: VEHICLE_SEARCH_SORT.RECENTLY_CONFIRMED,
        label: t("automotive.search.sortRecentlyConfirmed"),
      },
    ],
    filterChips: {
      heading: t("automotive.search.activeFiltersHeading"),
      clearAll: t("automotive.search.clearAllFilters"),
      removeAria: t("automotive.search.removeFilterAria"),
    },
    chipKeywordPrefix: t("automotive.search.chipKeywordPrefix"),
    chipAutomatic: t("automotive.search.chipAutomatic"),
    chipManual: t("automotive.search.chipManual"),
    chipNoMin: t("automotive.search.chipNoMin"),
    chipNoMax: t("automotive.search.chipNoMax"),
    chipVerifiedDealers: t("automotive.search.chipVerifiedDealers"),
    chipLowRisk: t("automotive.search.chipLowRisk"),
    chipRecentlyConfirmed: t("automotive.search.chipRecentlyConfirmed"),
    chipWithPhotos: t("automotive.search.chipWithPhotos"),
    chipCompleteInfo: t("automotive.search.chipCompleteInfo"),
    chipAvailabilityConfirmed: t("automotive.search.chipAvailabilityConfirmed"),
    chipBodySuv: t("automotive.search.chipBodySuv"),
    chipBodyAutomatic: t("automotive.search.chipBodyAutomatic"),
    chipInCityTemplate: t("automotive.search.chipInCity"),
    resultsFoundTemplate: t("automotive.search.resultsFound"),
    resultsQualifierVehicles: t("automotive.search.resultsQualifierVehicles"),
    resultsVerifiedSubtitle: t("automotive.search.resultsVerifiedSubtitle"),
    resultsAvailableTitle: t("automotive.search.resultsAvailableTitle"),
    resultsVisibleCountTemplate: t("automotive.search.resultsVisibleCount"),
    resultsNoMatchForTemplate: t("automotive.search.resultsNoMatchFor"),
    resultsNoMatchSubtitle: t("automotive.search.resultsNoMatchSubtitle"),
    chipMileageUpToTemplate: t("automotive.search.chipMileageUpTo"),
    chipYearFromTemplate: t("automotive.search.chipYearFrom"),
    chipYearToTemplate: t("automotive.search.chipYearTo"),
    filterStack: {
      quickBudget: t("automotive.search.filterStackQuickBudget"),
      city: t("automotive.search.filterStackCity"),
      make: t("automotive.search.filterStackMake"),
      model: t("automotive.search.filterStackModel"),
      minPrice: t("automotive.search.filterStackMinPrice"),
      maxPrice: t("automotive.search.filterStackMaxPrice"),
      yearFrom: t("automotive.search.filterStackYearFrom"),
      mileageMax: t("automotive.search.filterStackMileageMax"),
      bodyType: t("automotive.search.filterStackBodyType"),
      transmission: t("automotive.search.filterStackTransmission"),
      fuelType: t("automotive.fuelType"),
      fairPrice: t("automotive.search.filterStackFairPrice"),
      riskLevel: t("automotive.search.filterStackRiskLevel"),
      allMakes: t("automotive.search.filterStackAllMakes"),
      allModels: t("automotive.search.filterStackAllModels"),
      selectMake: t("automotive.search.filterStackSelectMake"),
      allBodyTypes: t("automotive.search.filterStackAllBodyTypes"),
      allOptions: t("automotive.search.allTypes"),
      allTransmissions: t("automotive.search.filterStackAllTransmissions"),
      allFuelTypes: t("automotive.search.filterStackAllFuelTypes"),
      verifiedOnly: t("automotive.search.filterStackVerifiedOnly"),
      hideRisk: t("automotive.search.filterStackHideRisk"),
      completeInfo: t("automotive.search.filterStackCompleteInfo"),
      recentlyConfirmed: t("automotive.search.filterStackRecentlyConfirmed"),
      hasPhotos: t("automotive.search.filterStackHasPhotos"),
      allColombia: t("automotive.search.allColombia"),
      close: t("automotive.search.close"),
      mobileApply: t("automotive.search.mobileFilterApply"),
      mobileCloseAria: t("automotive.search.mobileFilterCloseAria"),
      mileageOptions: [
        { label: t("automotive.search.filterStackMileageAny"), value: null },
        { label: t("automotive.search.filterStackMileageUpTo80k"), value: 80000 },
        { label: t("automotive.search.filterStackMileageUpTo120k"), value: 120000 },
        { label: t("automotive.search.filterStackMileageUpTo160k"), value: 160000 },
      ],
      minPriceOptions,
      maxPriceOptions: maxPriceOptions.map((option) =>
        option.value >= 999_999_999 ? { ...option, value: 999_999_999 } : option
      ),
      yearMinOptions: [
        { label: t("automotive.search.filterStackAnyYear"), value: 0 },
        { label: "2018", value: 2018 },
        { label: "2020", value: 2020 },
        { label: "2022", value: 2022 },
      ],
      transmissionOptions: [
        { value: null, label: t("automotive.search.filterStackAllTransmissions") },
        { value: "automatic", label: t("automotive.search.bodyAutomatic") },
        { value: "manual", label: t("automotive.search.bodyManual") },
        { value: "cvt", label: t("automotive.search.filterStackTransmissionCvt") },
      ],
      fuelOptions: [
        { value: null, label: t("automotive.search.filterStackAllFuelTypes") },
        { value: LISTING_FUEL_TYPE.GASOLINE, label: t("automotive.listing.fuel.gasoline") },
        { value: LISTING_FUEL_TYPE.DIESEL, label: t("automotive.listing.fuel.diesel") },
        { value: LISTING_FUEL_TYPE.HYBRID, label: t("automotive.listing.fuel.hybrid") },
        { value: LISTING_FUEL_TYPE.ELECTRIC, label: t("automotive.listing.fuel.electric") },
      ],
      fuelLabels: {
        [LISTING_FUEL_TYPE.GASOLINE]: t("automotive.listing.fuel.gasoline"),
        [LISTING_FUEL_TYPE.GAS]: t("automotive.listing.fuel.gasoline"),
        [LISTING_FUEL_TYPE.DIESEL]: t("automotive.listing.fuel.diesel"),
        [LISTING_FUEL_TYPE.HYBRID]: t("automotive.listing.fuel.hybrid"),
        [LISTING_FUEL_TYPE.ELECTRIC]: t("automotive.listing.fuel.electric"),
      },
      fairPriceOptions: [
        { value: "below_market", label: t("automotive.search.filterStackFairBelowMarket") },
        { value: "fair_price", label: t("automotive.search.filterStackFairFairPrice") },
        { value: "slightly_high", label: t("automotive.search.filterStackFairSlightlyHigh") },
        { value: "high_price", label: t("automotive.search.filterStackFairHighPrice") },
        { value: "not_enough_data", label: t("automotive.search.filterStackFairNotEnoughData") },
      ],
      riskOptions: [
        { value: "low", label: t("automotive.search.filterStackRiskLow") },
        { value: "medium", label: t("automotive.search.filterStackRiskMedium") },
        { value: "high", label: t("automotive.search.filterStackRiskHigh") },
        { value: "unknown", label: t("automotive.search.filterStackRiskUnknown") },
      ],
      budgetOptions,
      fairPriceLabels: {
        below_market: t("automotive.search.filterStackFairBelowMarket"),
        fair_price: t("automotive.search.filterStackFairFairPrice"),
        slightly_high: t("automotive.search.filterStackFairSlightlyHigh"),
        high_price: t("automotive.search.filterStackFairHighPrice"),
        not_enough_data: t("automotive.search.filterStackFairNotEnoughData"),
      },
      riskLabels: {
        low: t("automotive.search.filterStackRiskLow"),
        medium: t("automotive.search.filterStackRiskMedium"),
        high: t("automotive.search.filterStackRiskHigh"),
        unknown: t("automotive.search.filterStackRiskUnknown"),
      },
    },
    layout: {
      grid: t("automotive.layout.grid"),
      list: t("automotive.layout.list"),
      gridAria: t("automotive.layout.gridAria"),
      listAria: t("automotive.layout.listAria"),
    },
  };
}

export function getVehicleCardCopy(t) {
  const unknown = getUnknownAttributeLabel(t);
  return {
    specYear: t("automotive.card.specYear"),
    specMileage: t("automotive.card.specMileage"),
    specTransmission: t("automotive.card.specTransmission"),
    specBody: t("automotive.card.specBody"),
    viewDetail: t("automotive.card.viewDetail"),
    viewDetailAria: (title) => t("automotive.card.viewDetailAria", { title }),
    photos: (count) =>
      count === 1 ? t("automotive.card.photoSingular") : t("automotive.card.photos", { count }),
    contactWhatsapp: t("automotive.card.contactWhatsapp"),
    contact: t("automotive.card.contact"),
    contactUnavailable: t("automotive.card.contactUnavailable"),
    contactUnavailableDetail: t("automotive.card.contactUnavailableDetail"),
    whatsapp: t("automotive.card.whatsapp"),
    saveListing: t("automotive.card.saveListing"),
    savedListing: t("automotive.card.savedListing"),
    featured: t("automotive.card.featured"),
    dealerPending: t("automotive.card.dealerPending"),
    priceUnavailable: t("automotive.card.priceUnavailable"),
    mileageUnavailable: t("automotive.card.mileageUnavailable"),
    locationUnavailable: t("automotive.card.locationUnavailable"),
    fairPriceMoreInfo: (label) => t("automotive.badges.fairPriceMoreInfo", { label }),
    riskMoreInfo: t("automotive.badges.riskMoreInfo"),
    listingTransmission: (value) => {
      if (!value) return unknown;
      const key = `automotive.listing.transmission.${value}`;
      const label = t(key);
      return label !== key ? label : unknown;
    },
    listingBody: (value) => {
      if (!value) return unknown;
      const key = `automotive.listing.body.${value}`;
      const label = t(key);
      return label !== key ? label : unknown;
    },
  };
}

function formatCopInsightAmount(value, locale) {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `$${Number(value).toLocaleString(locale, { maximumFractionDigits: 0 })}`;
}

function formatCompactCopRangeInsight(min, max, locale) {
  const fmt = (v) => {
    const n = Number(v);
    if (n >= 1_000_000) {
      return `$${(n / 1_000_000).toLocaleString(locale, { maximumFractionDigits: 0 })}M`;
    }
    return formatCopInsightAmount(n, locale);
  };
  if (min != null && max != null) return `${fmt(min)}–${fmt(max)}`;
  return null;
}

export function formatCardPriceInsight(listing, t, locale = "es-CO") {
  if (!listing || !t) return null;

  const deltaCop = listing.fairPriceDeltaCop;
  if (deltaCop != null && !Number.isNaN(Number(deltaCop))) {
    const amount = formatCopInsightAmount(Math.abs(Number(deltaCop)), locale);
    if (amount) {
      return Number(deltaCop) < 0
        ? t("automotive.card.priceDeltaBelow", { amount })
        : t("automotive.card.priceDeltaAbove", { amount });
    }
  }

  const min =
    listing.comparableMinPriceCop ??
    listing.fairPriceComparableMinCop ??
    listing.comparableMinCop;
  const max =
    listing.comparableMaxPriceCop ??
    listing.fairPriceComparableMaxCop ??
    listing.comparableMaxCop;
  const range = formatCompactCopRangeInsight(min, max, locale);
  if (range) return t("automotive.card.comparableRange", { range });

  const median =
    listing.comparableMedianPriceCop ??
    listing.estimatedMarketPriceCop ??
    listing.marketPriceCop ??
    listing.estimatedFairPriceCop;
  if (median != null) {
    const n = Number(median);
    const price =
      n >= 1_000_000
        ? `$${(n / 1_000_000).toLocaleString(locale, { maximumFractionDigits: 0 })}M`
        : formatCopInsightAmount(n, locale);
    if (price) return t("automotive.card.comparableMedian", { price });
  }

  return null;
}

export function buildLocalizedVehicleSearchFilterChips(params, searchCopy, locale = "es-CO") {
  const { parseVehicleSearchParams } = require("../utils/vehicleSearch/vehicleSearchParamsUrl.js");
  const p = parseVehicleSearchParams(params);
  const chips = [];
  const fairPriceLabels = searchCopy?.filterStack?.fairPriceLabels || {};
  const riskLabels = searchCopy?.filterStack?.riskLabels || {};
  const formatKm = (value) => value.toLocaleString(locale);
  const formatPrice = searchCopy?.formatPriceChip || formatPriceChip;

  if (p.keyword) {
    chips.push({
      key: "keyword",
      label: `${searchCopy.chipKeywordPrefix} ${p.keyword}`.trim(),
    });
  }
  if (p.city) chips.push({ key: "city", label: p.city });
  if (p.make) chips.push({ key: "make", label: p.make });
  if (p.model) chips.push({ key: "model", label: p.model });
  if (p.bodyType) {
    chips.push({
      key: "bodyType",
      label: p.bodyType === "suv" ? searchCopy.chipBodySuv : p.bodyType.toUpperCase(),
    });
  }
  if (p.transmission) {
    const label =
      p.transmission === "automatic"
        ? searchCopy.chipAutomatic
        : p.transmission === "manual"
          ? searchCopy.chipManual
          : p.transmission;
    chips.push({ key: "transmission", label });
  }
  if (p.fuelType) {
    chips.push({
      key: "fuelType",
      label: searchCopy?.filterStack?.fuelLabels?.[p.fuelType] || p.fuelType,
    });
  }
  p.fuelTypes?.forEach((fuelType) => {
    chips.push({
      key: `fuel:${fuelType}`,
      removeKey: "fuelTypeValue",
      removeValue: fuelType,
      label: searchCopy?.filterStack?.fuelLabels?.[fuelType] || fuelType,
    });
  });

  if (p.priceMinCop != null || p.priceMaxCop != null) {
    const minLabel = p.priceMinCop ? formatPrice(p.priceMinCop) : searchCopy.chipNoMin;
    const maxLabel = p.priceMaxCop ? formatPrice(p.priceMaxCop) : searchCopy.chipNoMax;
    chips.push({ key: "price", label: `${minLabel} – ${maxLabel}` });
  }

  if (p.mileageMaxKm != null) {
    chips.push({
      key: "mileageMaxKm",
      label: searchCopy.chipMileageUpToTemplate.replace("{km}", formatKm(p.mileageMaxKm)),
    });
  }
  if (p.yearMin != null) {
    chips.push({
      key: "yearMin",
      label: searchCopy.chipYearFromTemplate.replace("{year}", String(p.yearMin)),
    });
  }
  if (p.yearMax != null) {
    chips.push({
      key: "yearMax",
      label: searchCopy.chipYearToTemplate.replace("{year}", String(p.yearMax)),
    });
  }

  if (p.dealerVerified) chips.push({ key: "dealerVerified", label: searchCopy.chipVerifiedDealers });

  p.fairPriceLabels?.forEach((label) => {
    chips.push({
      key: `fairPrice:${label}`,
      removeKey: "fairPriceLabel",
      removeValue: label,
      label: fairPriceLabels[label] || label,
    });
  });

  p.riskLevels?.forEach((level) => {
    chips.push({
      key: `risk:${level}`,
      removeKey: "riskLevel",
      removeValue: level,
      label: riskLabels[level] || level,
    });
  });

  if (p.hideHighRisk) chips.push({ key: "hideHighRisk", label: searchCopy.chipLowRisk });
  if (p.recentlyConfirmedOnly) {
    chips.push({ key: "recentlyConfirmedOnly", label: searchCopy.chipRecentlyConfirmed });
  }
  if (p.completeInfoOnly) chips.push({ key: "completeInfoOnly", label: searchCopy.chipCompleteInfo });
  if (p.availabilityStatus) {
    chips.push({ key: "availabilityStatus", label: searchCopy.chipAvailabilityConfirmed });
  }

  return chips;
}

export function buildLocalizedFilteredResultStatus(params, pagination, visibleCount, searchCopy, locale = "es-CO") {
  const { parseVehicleSearchParams } = require("../utils/vehicleSearch/vehicleSearchParamsUrl.js");
  const p = parseVehicleSearchParams(params);
  const total = pagination?.totalCount ?? pagination?.total;
  const formatCount = (value) => value.toLocaleString(locale);

  const parts = [];
  if (p.bodyType === "suv") parts.push(searchCopy.chipBodySuv);
  else if (p.bodyType) parts.push(p.bodyType);
  if (p.transmission === "automatic") parts.push(searchCopy.chipBodyAutomatic);
  if (p.fuelType) {
    parts.push(searchCopy?.filterStack?.fuelLabels?.[p.fuelType] || p.fuelType);
  }
  if (p.make) parts.push(p.make);
  if (p.model) parts.push(p.model);
  if (p.city) parts.push(searchCopy.chipInCityTemplate.replace("{city}", p.city));

  if (total != null && total > 0) {
    const qualifier = parts.length ? parts.join(" ") : searchCopy.resultsQualifierVehicles;
    return {
      title: searchCopy.resultsFoundTemplate
        .replace("{count}", formatCount(total))
        .replace("{qualifier}", qualifier)
        .replace(/\s+/g, " ")
        .trim(),
      subtitle:
        visibleCount < total
          ? searchCopy.pagination.showing(visibleCount, total)
          : searchCopy.resultsVerifiedSubtitle,
      totalKnown: true,
    };
  }

  if (visibleCount > 0) {
    return {
      title: parts.length ? parts.join(" ") : searchCopy.resultsAvailableTitle,
      subtitle: searchCopy.resultsVisibleCountTemplate.replace("{count}", formatCount(visibleCount)),
      totalKnown: false,
    };
  }

  if (p.make || p.model || p.priceMaxCop) {
    const desc = [p.make, p.model, p.priceMaxCop ? (searchCopy?.formatPriceChip || formatPriceChip)(p.priceMaxCop) : null]
      .filter(Boolean)
      .join(" ");
    return {
      title: searchCopy.resultsNoMatchForTemplate.replace("{desc}", desc),
      subtitle: searchCopy.resultsNoMatchSubtitle,
      totalKnown: false,
    };
  }

  return {
    title: searchCopy.empty.title,
    subtitle: searchCopy.empty.body,
    totalKnown: false,
  };
}

export function getHomeUiCopy(t, { formatMoney, displayCurrency } = {}) {
  const formatAmount = (amountCop) => {
    if (amountCop == null || Number.isNaN(Number(amountCop))) return "";
    if (typeof formatMoney === "function") {
      return formatMoney(Number(amountCop), { displayCurrency, showApproximation: false });
    }
    return formatTemplatePrice(amountCop, displayCurrency || "COP", { formatMoney });
  };
  const budgetOptions = buildSearchBudgetOptions({ t, formatAmount });

  return {
    heroTitle: t("automotive.home.heroTitle"),
    heroEyebrow: t("automotive.home.heroEyebrow"),
    heroBody: t("automotive.home.heroBody"),
    searchPlaceholder: t("automotive.home.searchPlaceholder"),
    loadingInventory: t("automotive.home.loadingInventory"),
    errorInventory: t("automotive.home.errorInventory"),
    emptyInventory: t("automotive.home.emptyInventory"),
    featuredDealers: t("automotive.home.featuredDealers"),
    viewDealerProfile: t("automotive.home.viewDealerProfile"),
    viewAllInventory: t("automotive.home.viewAllInventory"),
    trustKicker: t("automotive.home.trustKicker"),
    trustTitle: t("automotive.home.trustTitle"),
    trustBody: t("automotive.home.trustBody"),
    trustAvailabilityTitle: t("automotive.home.trustAvailabilityTitle"),
    trustAvailabilityBody: t("automotive.home.trustAvailabilityBody"),
    trustPriceTitle: t("automotive.home.trustPriceTitle"),
    trustPriceBody: t("automotive.home.trustPriceBody"),
    trustDealerTitle: t("automotive.home.trustDealerTitle"),
    trustDealerBody: t("automotive.home.trustDealerBody"),
    verifiedExplainer: t("automotive.home.verifiedExplainer"),
    dealerCaptionTitle: t("automotive.home.dealerCaptionTitle"),
    dealerCaptionBody: t("automotive.home.dealerCaptionBody"),
    dealerPanelEyebrow: t("automotive.home.dealerPanelEyebrow"),
    browseDealers: t("automotive.home.browseDealers"),
    dealerSignUp: t("automotive.home.dealerSignUp"),
    dealerImageAlt: t("automotive.home.dealerImageAlt"),
    howKicker: t("automotive.home.howKicker"),
    howTitle: t("automotive.home.howTitle"),
    howBody: t("automotive.home.howBody"),
    stepSearchTitle: t("automotive.home.stepSearchTitle"),
    stepSearchBody: t("automotive.home.stepSearchBody"),
    stepCompareTitle: t("automotive.home.stepCompareTitle"),
    stepCompareBody: t("automotive.home.stepCompareBody"),
    stepAskTitle: t("automotive.home.stepAskTitle"),
    stepAskBody: t("automotive.home.stepAskBody"),
    stepDecideTitle: t("automotive.home.stepDecideTitle"),
    stepDecideBody: t("automotive.home.stepDecideBody"),
    ctaTitle: t("automotive.home.ctaTitle"),
    ctaBody: t("automotive.home.ctaBody"),
    ctaButton: t("automotive.home.ctaButton"),
    primaryCtaLabel: t("automotive.home.primaryCtaLabel"),
    secondaryCtaLabel: t("automotive.home.secondaryCtaLabel"),
    footerTagline: t("automotive.home.footerTagline"),
    footerUsedCars: t("automotive.home.footerUsedCars"),
    footerChecklist: t("automotive.home.footerChecklist"),
    footerSellCar: t("automotive.home.footerSellCar"),
    footerPrivacy: t("automotive.home.footerPrivacy"),
    footerTerms: t("automotive.home.footerTerms"),
    transmissionAbbr: t("automotive.home.transmissionAbbr"),
    engineAbbr: t("automotive.home.engineAbbr"),
    searchCardTitle: t("automotive.home.searchCardTitle"),
    searchLabel: t("automotive.home.searchLabel"),
    typeLabel: t("automotive.home.typeLabel"),
    fuelLabel: t("automotive.home.fuelLabel"),
    viewInventory: t("automotive.home.viewInventory"),
    bodyOptions: [
      { value: "", label: t("automotive.home.allTypes") },
      { value: "suv", label: t("automotive.listing.body.suv") },
      { value: "sedan", label: t("automotive.listing.body.sedan") },
      { value: "hatchback", label: t("automotive.listing.body.hatchback") },
      { value: "pickup", label: t("automotive.listing.body.pickup") },
    ],
    fuelOptions: [
      { value: "", label: t("automotive.home.allFuels") },
      { value: LISTING_FUEL_TYPE.GASOLINE, label: t("automotive.listing.fuel.gasoline") },
      { value: LISTING_FUEL_TYPE.DIESEL, label: t("automotive.listing.fuel.diesel") },
      { value: LISTING_FUEL_TYPE.HYBRID, label: t("automotive.listing.fuel.hybrid") },
      { value: LISTING_FUEL_TYPE.ELECTRIC, label: t("automotive.listing.fuel.electric") },
    ],
    cityLabel: t("automotive.home.cityLabel"),
    budgetLabel: t("automotive.home.budgetLabel"),
    viewInventory: t("automotive.home.viewInventory"),
    allCities: t("automotive.home.allCities"),
    proofVehicles: t("automotive.home.proofVehicles"),
    proofDealers: t("automotive.home.proofDealers"),
    proofResponse: t("automotive.home.proofResponse"),
    budgetOptions,
  };
}

export function getHowItWorksPageCopy(t) {
  return {
    seoTitle: t("automotive.howItWorksPage.seoTitle"),
    seoDescription: t("automotive.howItWorksPage.seoDescription"),
    heroEyebrow: t("automotive.howItWorksPage.heroEyebrow"),
    heroTitle: t("automotive.howItWorksPage.heroTitle"),
    heroBody: t("automotive.howItWorksPage.heroBody"),
    processKicker: t("automotive.howItWorksPage.processKicker"),
    processTitle: t("automotive.howItWorksPage.processTitle"),
    processIntro: t("automotive.howItWorksPage.processIntro"),
    steps: [
      {
        title: t("automotive.howItWorksPage.step1Title"),
        body: t("automotive.howItWorksPage.step1Body"),
      },
      {
        title: t("automotive.howItWorksPage.step2Title"),
        body: t("automotive.howItWorksPage.step2Body"),
      },
      {
        title: t("automotive.howItWorksPage.step3Title"),
        body: t("automotive.howItWorksPage.step3Body"),
      },
      {
        title: t("automotive.howItWorksPage.step4Title"),
        body: t("automotive.howItWorksPage.step4Body"),
      },
    ],
    winnersKicker: t("automotive.howItWorksPage.winnersKicker"),
    winnersTitle: t("automotive.howItWorksPage.winnersTitle"),
    winnersIntro: t("automotive.howItWorksPage.winnersIntro"),
    winners: [
      {
        title: t("automotive.howItWorksPage.winners.buyers.title"),
        body: t("automotive.howItWorksPage.winners.buyers.body"),
      },
      {
        title: t("automotive.howItWorksPage.winners.dealers.title"),
        body: t("automotive.howItWorksPage.winners.dealers.body"),
      },
      {
        title: t("automotive.howItWorksPage.winners.community.title"),
        body: t("automotive.howItWorksPage.winners.community.body"),
      },
    ],
    ctaTitle: t("automotive.howItWorksPage.ctaTitle"),
    ctaBody: t("automotive.howItWorksPage.ctaBody"),
    ctaInventory: t("automotive.howItWorksPage.ctaInventory"),
    ctaQuiz: t("automotive.howItWorksPage.ctaQuiz"),
    ctaDealers: t("automotive.howItWorksPage.ctaDealers"),
    ctaPartner: t("automotive.howItWorksPage.ctaPartner"),
  };
}

export function getQuizUiCopy(t, { formatMoney, displayCurrency, locale } = {}) {
  const trustItemKeys = [
    "verifiedDealers",
    "confirmedListings",
    "priceCompare",
    "whatsappContact",
  ];

  const formatBudgetAmount = (amountCop) =>
    formatCompactBudgetAmount(amountCop, { locale: locale || "en-US" });

  const budgetOptions = BUDGET_OPTION_DEFS.map((entry) => {
    let label;
    if (entry.priceMin === 0 && entry.priceMax != null) {
      label = t("automotive.quiz.budgetUpTo", { amount: formatBudgetAmount(entry.priceMax) });
    } else if (entry.priceMax == null) {
      label = t("automotive.quiz.budgetOver", { amount: formatBudgetAmount(entry.priceMin) });
    } else {
      label = t("automotive.quiz.budgetRange", {
        min: formatBudgetAmount(entry.priceMin),
        max: formatBudgetAmount(entry.priceMax),
      });
    }
    return { ...entry, label };
  });

  const mapOptionValues = (prefix, values) =>
    values.map((value) => ({
      value,
      label: t(`automotive.quiz.options.${prefix}.${value}`),
    }));

  const cityOptions = CITY_OPTION_VALUES.map((value) => ({
    value,
    label: value === "Otra" ? t("automotive.quiz.options.cityOther") : value,
  }));

  return {
    seoTitle: t("automotive.quiz.seoTitle"),
    seoDescription: t("automotive.quiz.seoDescription"),
    heroEyebrow: t("automotive.quiz.heroEyebrow"),
    heroTitle: t("automotive.quiz.heroTitle"),
    heroBody: t("automotive.quiz.heroBody"),
    continue: t("automotive.quiz.continue"),
    seeRecommendations: t("automotive.quiz.seeRecommendations"),
    submitting: t("automotive.quiz.submitting"),
    back: t("automotive.quiz.back"),
    progressStep: (current, total) =>
      t("automotive.quiz.progressStep", { current, total }),
    footerTagline: t("automotive.quiz.footerTagline"),
    heroTrust: [
      t("automotive.quiz.heroTrustNoPressure"),
      t("automotive.quiz.heroTrustVerified"),
      t("automotive.quiz.heroTrustCompare"),
    ],
    consentShare: (siteName) => t("automotive.quiz.consentShare", { siteName }),
    whatsappPlaceholder: t("automotive.quiz.whatsappPlaceholder"),
    emailPlaceholder: t("automotive.quiz.emailPlaceholder"),
    fields: {
      customBudgetLabel: t("automotive.quiz.fields.customBudgetLabel"),
      customBudgetPlaceholder: t("automotive.quiz.fields.customBudgetPlaceholder"),
      neighborhoodLabel: t("automotive.quiz.fields.neighborhoodLabel"),
      neighborhoodPlaceholder: t("automotive.quiz.fields.neighborhoodPlaceholder"),
      transmissionGroup: t("automotive.quiz.fields.transmissionGroup"),
      fuelGroup: t("automotive.quiz.fields.fuelGroup"),
      downPaymentLabel: t("automotive.quiz.fields.downPaymentLabel"),
      downPaymentPlaceholder: t("automotive.quiz.fields.downPaymentPlaceholder"),
      loanTermGroup: t("automotive.quiz.fields.loanTermGroup"),
      nameLabel: t("automotive.quiz.fields.nameLabel"),
      namePlaceholder: t("automotive.quiz.fields.namePlaceholder"),
      whatsappLabel: t("automotive.quiz.fields.whatsappLabel"),
      emailLabel: t("automotive.quiz.fields.emailLabel"),
    },
    validation: {
      budget: t("automotive.quiz.validation.budget"),
      location: t("automotive.quiz.validation.location"),
      vehicleType: t("automotive.quiz.validation.vehicleType"),
      useCase: t("automotive.quiz.validation.useCase"),
      transmission: t("automotive.quiz.validation.transmission"),
      fuel: t("automotive.quiz.validation.fuel"),
      financing: t("automotive.quiz.validation.financing"),
      loanTerm: t("automotive.quiz.validation.loanTerm"),
      timeline: t("automotive.quiz.validation.timeline"),
      name: t("automotive.quiz.validation.name"),
      whatsapp: t("automotive.quiz.validation.whatsapp"),
      consent: t("automotive.quiz.validation.consent"),
    },
    budgetOptions,
    cityOptions,
    vehicleTypeOptions: mapOptionValues("vehicleType", VEHICLE_TYPE_OPTION_VALUES),
    useCaseOptions: mapOptionValues("useCase", USE_CASE_OPTION_VALUES),
    transmissionOptions: mapOptionValues("transmission", TRANSMISSION_OPTION_VALUES),
    fuelOptions: mapOptionValues("fuel", FUEL_OPTION_VALUES),
    financingOptions: mapOptionValues("financing", FINANCING_OPTION_VALUES),
    loanTermOptions: LOAN_TERM_OPTION_VALUES.map((months) => ({
      value: months,
      label: t("automotive.preferences.termOption", { months }),
    })),
    timelineOptions: mapOptionValues("timeline", TIMELINE_OPTION_VALUES),
    optionLabel: (group, value) =>
      value ? t(`automotive.quiz.options.${group}.${value}`) : "—",
    summaryFields: {
      budget: t("automotive.quiz.summaryFields.budget"),
      vehicleType: t("automotive.quiz.summaryFields.vehicleType"),
      useCase: t("automotive.quiz.summaryFields.useCase"),
      financing: t("automotive.quiz.summaryFields.financing"),
      downPayment: t("automotive.quiz.summaryFields.downPayment"),
      loanTerm: t("automotive.quiz.summaryFields.loanTerm"),
      timeline: t("automotive.quiz.summaryFields.timeline"),
      transmission: t("automotive.quiz.summaryFields.transmission"),
      fuel: t("automotive.quiz.summaryFields.fuel"),
    },
    leadTemperature: {
      hot: t("automotive.quiz.leadTemperature.hot"),
      warm: t("automotive.quiz.leadTemperature.warm"),
      mild: t("automotive.quiz.leadTemperature.mild"),
      cool: t("automotive.quiz.leadTemperature.cool"),
    },
    formatCustomBudgetLabel: (amountCop) =>
      t("automotive.quiz.budgetCustomUpTo", { amount: formatBudgetAmount(amountCop) }),
    buildNarrative: (intent, summary) => buildQuizNarrative(t, intent, summary),
    steps: (stepIds) =>
      stepIds.map((step) => ({
        ...step,
        title: t(`automotive.quiz.steps.${step.id}.title`),
        subtitle: t(`automotive.quiz.steps.${step.id}.subtitle`),
      })),
    trustItems: trustItemKeys.map((key) => ({
      title: t(`automotive.quiz.trustItems.${key}.title`),
      text: t(`automotive.quiz.trustItems.${key}.text`),
    })),
    resultsSeoTitle: t("automotive.quiz.resultsSeoTitle"),
    resultsSeoDescription: t("automotive.quiz.resultsSeoDescription"),
    loadError: t("automotive.quiz.loadError"),
    fallbackError: t("automotive.quiz.fallbackError"),
    compareSelected: t("automotive.quiz.compareSelected"),
    compare: t("automotive.nav.compare"),
    sendRequest: t("automotive.quiz.sendRequest"),
    sending: t("automotive.quiz.sending"),
    requestReceived: t("automotive.quiz.requestReceived"),
    requestWhatsapp: t("automotive.quiz.requestWhatsapp"),
    results: {
      heroEyebrow: t("automotive.quiz.results.heroEyebrow"),
      heroTitle: t("automotive.quiz.results.heroTitle"),
      heroBody: t("automotive.quiz.results.heroBody"),
      emptyTitle: t("automotive.quiz.results.emptyTitle"),
      emptyBody: t("automotive.quiz.results.emptyBody"),
      emptyCta: t("automotive.quiz.results.emptyCta"),
      loading: t("automotive.quiz.results.loading"),
      errorTitle: t("automotive.quiz.results.errorTitle"),
      retry: t("automotive.quiz.results.retry"),
      profileTitle: t("automotive.quiz.results.profileTitle"),
      readinessLabel: t("automotive.quiz.results.readinessLabel"),
      confirmTitle: t("automotive.quiz.results.confirmTitle"),
      confirmBody: t("automotive.quiz.results.confirmBody"),
      keepComparing: t("automotive.quiz.results.keepComparing"),
      editPreferences: t("automotive.quiz.results.editPreferences"),
      routingTitle: t("automotive.quiz.results.routingTitle"),
      routingBody: t("automotive.quiz.results.routingBody"),
      checklistBanner: t("automotive.quiz.results.checklistBanner"),
      checklistCta: t("automotive.quiz.results.checklistCta"),
      checklistLink: t("automotive.quiz.results.checklistLink"),
      financingTitle: t("automotive.quiz.results.financingTitle"),
      financingBody: t("automotive.quiz.results.financingBody"),
      financingCta: t("automotive.quiz.results.financingCta"),
      partialMatch: t("automotive.quiz.results.partialMatch"),
      noMatchesTitle: t("automotive.quiz.results.noMatchesTitle"),
      noMatchesBody: t("automotive.quiz.results.noMatchesBody"),
      exploreMore: t("automotive.quiz.results.exploreMore"),
      viewInventory: t("automotive.quiz.results.viewInventory"),
      retakeQuiz: t("automotive.quiz.results.retakeQuiz"),
      compareCount: (count) => t("automotive.quiz.results.compareCount", { count }),
      stickyCta: t("automotive.quiz.results.stickyCta"),
      closeFeedback: t("automotive.quiz.results.closeFeedback"),
      matchScore: (score) => t("automotive.quiz.results.matchScore", { score }),
      viewDetail: t("automotive.quiz.results.viewDetail"),
      viewInventoryCard: t("automotive.quiz.results.viewInventoryCard"),
      contactWhatsapp: t("automotive.quiz.results.contactWhatsapp"),
      saved: t("automotive.quiz.results.saved"),
      save: t("automotive.quiz.results.save"),
      inComparison: t("automotive.quiz.results.inComparison"),
      compareAction: t("automotive.quiz.results.compareAction"),
    },
  };
}

function buildQuizNarrative(t, intent, summary) {
  if (!intent) return "";

  const type = t(`automotive.quiz.options.vehicleType.${intent.vehicleType || "unsure"}`);
  const transmission =
    intent.transmission === "automatico"
      ? t("automotive.quiz.narrative.transmissionAutomatic")
      : intent.transmission === "mecanico"
        ? t("automotive.quiz.narrative.transmissionManual")
        : "";
  const locationLabel = t("automotive.quiz.narrative.metroRegion");
  const budget = summary?.budget || t("automotive.quiz.narrative.yourBudget");
  const useCases = (intent.useCases || [])
    .map((value) => t(`automotive.quiz.options.useCase.${value}`).toLowerCase())
    .filter(Boolean)
    .slice(0, 2);

  const parts = [
    t("automotive.quiz.narrative.searching", {
      type,
      transmission,
      location: locationLabel,
      budget: String(budget).toLowerCase(),
    }),
  ];
  if (useCases.length) {
    parts.push(t("automotive.quiz.narrative.idealFor", { uses: useCases.join(", ") }));
  }
  const timelineMap = {
    today: t("automotive.quiz.narrative.timelineToday"),
    this_month: t("automotive.quiz.narrative.timelineThisMonth"),
    "1-3_months": t("automotive.quiz.narrative.timeline1To3"),
    researching: t("automotive.quiz.narrative.timelineResearching"),
  };
  const timeline = timelineMap[intent.purchaseTimeline];
  if (timeline) parts.push(timeline);

  return `${parts.join(", ")}.`;
}
